import React, { useEffect } from "react"
import { OreveButton } from "components/button/OreveButton.tsx"
import { ArrowBack } from "@mui/icons-material"
import { MainPageContentContainer } from "components/MainPageContentContainer"
import {
    LinearProgress,
    Typography,
    Divider,
    useMediaQuery,
    Box,
} from "@mui/material"
import Activation from "assets/svg/Type=Activer une carte RFID.svg"
import { BackendSchema } from "layouts/charging-rules/interface/Rules.ts"
import { Loader } from "components/Loader.tsx"
import { ChargingRulesGlobalComponent } from "layouts/charging-rules/components/ChargingRulesGlobalComponent.tsx"
import { useChargingRulesContext } from "layouts/charging-rules/context/useChargingRulesContext.ts"
import { transformRulesToBackendSchema } from "layouts/charging-rules/utils/utils.ts"

interface Step3Props {
    goBackToList: () => void
    setRuleRfidCard: React.Dispatch<React.SetStateAction<BackendSchema>>
    validateRule: () => void
}
export const Step3: React.FC<Step3Props> = ({
    goBackToList,
    setRuleRfidCard,
    validateRule,
}) => {
    return (
        <MainPageContentContainer
            sx={{
                position: "absolute",
                padding: "0px",
                margin: "0px",
                width: "calc(100vw - (1.5rem + 1rem))",
                height: "calc(100vh - (1.5rem + 132px + 2.5rem ))",
            }}
        >
            <OreveButton
                sx={{
                    position: "absolute",
                    marginTop: "10px",
                    textDecoration: "none",
                    "&:hover, &:focus": {
                        backgroundColor: "transparent",
                        textDecoration: "none",
                    },
                }}
                variant="text"
                value="Retour à la liste de cartes"
                onClick={goBackToList}
                icon={ArrowBack}
            />
            <ParameterRule
                setRuleRfidCard={setRuleRfidCard}
                validateRule={validateRule}
            />
        </MainPageContentContainer>
    )
}

export const ParameterRule = ({
    setRuleRfidCard,
    validateRule,
}: {
    setRuleRfidCard: React.Dispatch<React.SetStateAction<BackendSchema>>
    validateRule: () => void
}) => {
    const isTablet = useMediaQuery("(max-width:900px)")

    const {
        rules,
        listChargingRulesLoading,
        setIsRuleChanged,
        errorDuration,
        setErrorDuration,
        errorEnergy,
        setErrorEnergy,
        allHoursSelected,
        allDaysSelected,
        noDaysSelected,
        noHoursSelected,
    } = useChargingRulesContext()

    useEffect(() => {
        return () => {
            setErrorDuration(false)
            setErrorEnergy(false)
        }
    }, [])

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                width: "100%",
                height: "100%",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    overflowY: "auto",
                    height: "100%",
                    width: "75vw",
                    mx: "3.125rem",
                    mt: "2.5rem",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="labelLarge" sx={{ mb: "7px" }}>
                        3/4
                    </Typography>
                    <LinearProgress
                        sx={{
                            width: "100px",
                            borderRadius: "20px",
                            bgcolor: "grey.100",
                        }}
                        color="secondary"
                        variant="determinate"
                        value={75}
                    />
                    <Typography variant="h3" sx={{ my: "1rem" }}>
                        Activer une carte
                    </Typography>
                    <Typography variant="labelLarge" sx={{ mb: "2.5rem" }}>
                        Définissez les règles de recharge qui s’appliqueront à
                        cette carte
                    </Typography>
                </Box>
                {listChargingRulesLoading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            backgroundColor: "#fff",
                            paddingX: "2.25rem",
                            paddingY: "1.5rem",
                            borderRadius: "20px",
                            border: "none",
                        }}
                    >
                        <Loader />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            gap: "2.5rem",
                            ml: "1.5rem",
                        }}
                    >
                        {rules.map((rule, index) => (
                            <Box key={index}>
                                <ChargingRulesGlobalComponent
                                    key={rule.type}
                                    rule={rule}
                                    isRfid={true}
                                />
                                {isTablet && index < rules.length - 1 && (
                                    <Divider sx={{ mt: "1rem" }} />
                                )}
                            </Box>
                        ))}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                width: "100%",
                                mt: 4,
                                mb: 2,
                            }}
                        >
                            <OreveButton
                                data-cy="rfid__activation--validate"
                                value="Valider"
                                variant="contained"
                                sx={{
                                    bottom: "40px",
                                }}
                                disabled={
                                    allDaysSelected ||
                                    allHoursSelected ||
                                    errorDuration ||
                                    errorEnergy ||
                                    noDaysSelected ||
                                    noHoursSelected
                                }
                                onClick={() => {
                                    const result =
                                        transformRulesToBackendSchema(rules)
                                    setRuleRfidCard(result)
                                    validateRule()
                                    setIsRuleChanged(true)
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
            <Box
                sx={{
                    display: { xs: "none", md: "flex" },
                    bgcolor: "lightgrey.main",
                    borderBottomRightRadius: "20px",
                    borderTopRightRadius: "20px",
                    width: { md: "25vw", lg: "30vw" },
                    minWidth: "300px",
                    height: "calc(100vh - (1.5rem + 132px + 2.5rem ))",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                    "& img": {
                        width: "100%",
                        height: "auto",
                        maxWidth: "100%",
                        objectFit: "contain",
                        padding: "2rem",
                    },
                }}
            >
                <img src={Activation} alt="" />
            </Box>
        </Box>
    )
}
